// TAPINTO® Editor Frontend - Component Library
// Version: 1.0
// Date: 2025-10-01

import React, { useState } from 'react';
import { useEditor } from '@craftjs/core';
import { 
  Type, 
  Image, 
  Square, 
  Layout, 
  ChevronDown, 
  ChevronRight,
  Search
} from 'lucide-react';
import { Input } from '../common/Input';
import { Hero } from './components/Hero';
import { Text } from './components/Text';
import { Image as ImageComponent } from './components/Image';
import { Button as ButtonComponent } from './components/Button';
import { TwoColumn } from './components/TwoColumn';

interface ComponentCategory {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  components: ComponentItem[];
}

interface ComponentItem {
  id: string;
  name: string;
  component: React.ComponentType<any>;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const componentCategories: ComponentCategory[] = [
  {
    id: 'layout',
    name: 'Layout',
    icon: Layout,
    components: [
      {
        id: 'two-column',
        name: 'Two Column',
        component: TwoColumn,
        icon: Layout,
        description: 'Two column layout with customizable widths',
      },
    ],
  },
  {
    id: 'content',
    name: 'Content',
    icon: Type,
    components: [
      {
        id: 'text',
        name: 'Text Block',
        component: Text,
        icon: Type,
        description: 'Add text content with formatting options',
      },
      {
        id: 'image',
        name: 'Image',
        component: ImageComponent,
        icon: Image,
        description: 'Add images with styling options',
      },
    ],
  },
  {
    id: 'interactive',
    name: 'Interactive',
    icon: Square,
    components: [
      {
        id: 'button',
        name: 'Button',
        component: ButtonComponent,
        icon: Square,
        description: 'Call-to-action button with links',
      },
    ],
  },
  {
    id: 'sections',
    name: 'Sections',
    icon: Layout,
    components: [
      {
        id: 'hero',
        name: 'Hero Section',
        component: Hero,
        icon: Layout,
        description: 'Full-width hero section with title and CTA',
      },
    ],
  },
];

export function ComponentLibrary() {
  const { connectors } = useEditor();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['layout', 'content'])
  );

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const filteredCategories = componentCategories.map(category => ({
    ...category,
    components: category.components.filter(component =>
      component.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      component.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.components.length > 0);

  const handleDragStart = (component: ComponentItem) => {
    // This will be handled by Craft.js connectors
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200">
        <h3 className="text-lg font-semibold text-neutral-900 mb-3">
          Components
        </h3>
        <Input
          placeholder="Search components..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Components List */}
      <div className="flex-1 overflow-y-auto">
        {filteredCategories.map((category) => (
          <div key={category.id} className="border-b border-neutral-100">
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <category.icon className="h-4 w-4 text-neutral-600" />
                <span className="font-medium text-neutral-900">
                  {category.name}
                </span>
                <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                  {category.components.length}
                </span>
              </div>
              {expandedCategories.has(category.id) ? (
                <ChevronDown className="h-4 w-4 text-neutral-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-neutral-500" />
              )}
            </button>

            {expandedCategories.has(category.id) && (
              <div className="pb-2">
                {category.components.map((component) => (
                  <div
                    key={component.id}
                    ref={(ref) => {
                      if (ref) {
                        connectors.create(ref, component.component);
                      }
                    }}
                    className="mx-4 mb-2 p-3 bg-white border border-neutral-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing"
                    draggable
                    onDragStart={() => handleDragStart(component)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <component.icon className="h-5 w-5 text-neutral-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-neutral-900">
                          {component.name}
                        </h4>
                        <p className="text-xs text-neutral-500 mt-1">
                          {component.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="p-8 text-center">
            <Search className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
            <p className="text-sm text-neutral-500">
              No components found matching "{searchQuery}"
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-neutral-200 bg-neutral-50">
        <p className="text-xs text-neutral-500 text-center">
          Drag components to the canvas to add them
        </p>
      </div>
    </div>
  );
}
